import { useCallback } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useApiRouter } from "../../hooks/useApiRouter";
import useRequest from "../../hooks/useRequest";
import { useApiError } from "../ReactHookForm/ApiError/ApiError";

/**
 * @typedef {object} TwoFactorAuthChallengeFormValues
 * @prop {object} data
 * @prop {string[]} data.code
 */

/**
 * @returns {TwoFactorAuthChallengeFormValues}
 */
function getDefaultValues() {
  return {
    data: {
      code: ["", "", "", "", "", ""],
    },
  };
}

/**
 * @typedef {object} Props
 * @prop {string} challengeId
 * @prop {() => void} [onSuccess]
 * @prop {(params: {form: import("react-hook-form").UseFormReturn<TwoFactorAuthChallengeFormValues>, submit: (event?: import("react").FormEvent<HTMLFormElement>) => void, isLoading: boolean}) => import("react").ReactElement} children
 */
/**
 * @param {Props} props
 */
export function TwoFactorAuthChallengeHandler({
  challengeId,
  onSuccess,
  children,
}) {
  const form = useForm({
    defaultValues: getDefaultValues(),
  });
  const { handleSubmit } = form;

  const { request, loading: isLoading } = useRequest();

  const { resolveApiError, resetApiErrors } = useApiError();

  const { apiRouter } = useApiRouter();

  const submit = useCallback(
    /**
     * @param {import("react").FormEvent<HTMLFormElement>} [event]
     */
    (event) => {
      event?.preventDefault();
      handleSubmit((values) => {
        const codeString = values.data.code.join("");
        if (codeString.length !== 6) {
          return;
        }
        const url = apiRouter.getUrl({
          id: "@api/user.twoFactorAuthentication.challenge@complete",
          parameters: {
            challengeId,
          },
        });

        request(url, {
          method: "POST",
          body: JSON.stringify({ data: { code: codeString } }),
          headers: {
            "Content-Type": "application/json",
          },
        }).then(async ({ response, parsedJson: data }) => {
          resetApiErrors();
          if (response?.ok) {
            onSuccess?.();
          } else {
            resolveApiError({ parsedJson: data, form });
          }
        });
      })();
    },
    [
      apiRouter,
      challengeId,
      form,
      handleSubmit,
      onSuccess,
      request,
      resetApiErrors,
      resolveApiError,
    ],
  );

  return (
    <FormProvider {...form}>
      {children({ form, submit, isLoading })}
    </FormProvider>
  );
}
